import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverOptions {
  threshold?: number;
  rootMargin?: string;
  freezeOnceVisible?: boolean;
}

/**
 * Netflix-style intersection observer hook
 * Detects when elements enter viewport and optionally "freezes" the visible state
 * This prevents images from unloading when scrolling back up
 */
export function useIntersectionObserver(
  options: UseIntersectionObserverOptions = {}
) {
  const {
    threshold = 0.1,
    rootMargin = '50px', // Load images slightly before they're visible
    freezeOnceVisible = true // Netflix-style: once visible, stays "visible" in state
  } = options;

  const elementRef = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // If already been visible and we're freezing, don't set up new observer
    if (freezeOnceVisible && hasBeenVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        
        if (isVisible) {
          setHasBeenVisible(true);
        }
        
        // If freezing and has been visible, keep it as "intersecting"
        if (freezeOnceVisible && hasBeenVisible) {
          setIsIntersecting(true);
        } else {
          setIsIntersecting(isVisible);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, freezeOnceVisible, hasBeenVisible]);

  return {
    ref: elementRef,
    isIntersecting: freezeOnceVisible ? (hasBeenVisible || isIntersecting) : isIntersecting,
    hasBeenVisible
  };
}

